<template>
  <div ref="hostRef" class="form-overlays-block" :class="rootClasses">
    <slot />
    <FormOverlayPage
      v-for="overlay in overlays"
      :key="overlay.id"
      :data="overlay"
      :form-id="formId"
    />
    <PlatformDisconnectBlockOverlay />
    <FormMessagesOverlay />
  </div>
</template>

<script setup lang="ts">
import { usePageData } from '@/common/composables/usePageData'
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId'
import FormMessagesOverlay from '@/components/platform/form/FormMessagesOverlay.vue'
import FormOverlayPage from '@/components/platform/form/FormOverlayPage.vue'
import PlatformDisconnectBlockOverlay from '@/components/platform/form/PlatformDisconnectBlockOverlay.vue'
import { isFormGrabVertical, resolveFormScrollOwner } from '@/common/utils/formScrollOwner'
import {
  FormOverlayHostKey,
  FormScrollOwnerKey,
  PlatformDialogLayoutKey,
} from '@/components/platform/injection-keys'
import { normalizeFormOverlays } from '@/services/parse/parse.service'
import type { FormOverlay } from '@/services/parse/types'
import { computed, inject, provide, ref, unref, watch } from 'vue'

const hostRef = ref<HTMLElement | null>(null)
provide(FormOverlayHostKey, hostRef)
const platformDialogLayout = inject(PlatformDialogLayoutKey, null)
const { pageData, pageDataSlot, pageRegistryFormId } = usePageData()

const formId = computed(() => {
  if (pageRegistryFormId.value) return String(pageRegistryFormId.value)
  return resolveElementRegistryFormId(pageData.value as any, String(pageDataSlot.value ?? ''))
})

const formObj = computed(() => {
  const f = (pageData.value as any)?.form
  if (!f || typeof f !== 'object') return {}
  const topHasLayout =
    (Array.isArray(f.main) && f.main.length > 0) ||
    (Array.isArray(f.header) && f.header.length > 0) ||
    (Array.isArray(f.footer) && f.footer.length > 0) ||
    Array.isArray(f.overlays) ||
    f.overlays?.overlay != null
  if (topHasLayout) return f
  const inner = f.form
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const innerHasLayout =
      (Array.isArray(inner.main) && inner.main.length > 0) ||
      (Array.isArray(inner.header) && inner.header.length > 0) ||
      (Array.isArray(inner.footer) && inner.footer.length > 0) ||
      inner.panels != null ||
      Array.isArray(inner.overlays) ||
      inner.overlays?.overlay != null
    if (innerHasLayout) return { ...f, ...inner }
  }
  return f
})

const dialogLayout = computed(() =>
  unref(platformDialogLayout as { sized?: boolean; compact?: boolean } | null),
)
const inDialog = computed(() => platformDialogLayout != null)
const formGrabVertical = computed(() => isFormGrabVertical(formObj.value))
const scrollOwner = computed(() =>
  resolveFormScrollOwner({
    formGrabVertical: formGrabVertical.value,
    dialogSized: Boolean(dialogLayout.value?.sized),
    inDialog: inDialog.value,
  }),
)
provide(FormScrollOwnerKey, scrollOwner)

const rootClasses = computed(() => {
  const layout = dialogLayout.value
  const owner = scrollOwner.value
  return {
    'form-overlays-block--dialog-compact': Boolean(layout?.compact),
    'form-overlays-block--dialog-sized': Boolean(layout?.sized),
    'form-overlays-block--viewport': !inDialog.value && owner === 'page',
    'form-overlays-block--scroll-form': owner === 'form',
    'form-overlays-block--scroll-page': owner === 'page',
  }
})

watch(
  [formId, () => (pageData.value as any)?.form],
  ([, root]) => {
    if (!root || typeof root !== 'object') return
    const fid = parseInt(formId.value, 10)
    normalizeFormOverlays(root, Number.isFinite(fid) ? fid : 0)
  },
  { immediate: true },
)

const overlays = computed((): FormOverlay[] => {
  const list = formObj.value?.overlays
  return Array.isArray(list) ? list : []
})
</script>

<style scoped lang="scss">
.form-overlays-block {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  /* PLATFORM_TABLE_FILL_MIN_BODY_PX — leftover floor (2717). */
  --platform-table-fill-min-body: 120px;
}

/* Document leftover: fill the slot; page (or --fill table) owns the bar. */
.form-overlays-block--viewport,
.form-overlays-block--scroll-form:not(.form-overlays-block--dialog-sized) {
  flex: 1 1 auto;
  min-height: 0;
}

.form-overlays-block--viewport {
  overflow: hidden;
}

.form-overlays-block--scroll-form:not(.form-overlays-block--dialog-sized) {
  overflow: auto;
  scrollbar-gutter: stable;
}

.form-overlays-block--viewport :deep(.form-top-actions),
.form-overlays-block--viewport :deep(.form-actions-bottom) {
  flex: 0 0 auto;
}

.form-overlays-block--viewport :deep(.form-section--header),
.form-overlays-block--viewport :deep(.form-section--footer) {
  flex: 0 0 auto;
  min-height: auto;
  overflow: visible;
}

.form-overlays-block--viewport :deep(.form-section--header .empty-tabs),
.form-overlays-block--viewport :deep(.form-section--footer .empty-tabs) {
  flex: 0 0 auto;
  min-height: auto;
  overflow: visible;
}

.form-overlays-block--viewport :deep(.form-main-body) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.form-overlays-block--viewport :deep(.multiple-main-root) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.form-overlays-block--scroll-form:not(.form-overlays-block--dialog-sized) :deep(.form-main-body) {
  flex: 0 0 auto;
  overflow: visible;
}

.form-overlays-block--dialog-compact :deep(.form-top-actions) {
  margin-bottom: 0.35rem;
  gap: 0.5rem;
}

.form-overlays-block--dialog-compact :deep(.m-expansion .p-accordionheader),
.form-overlays-block--dialog-compact :deep(.m-expansion .p-accordion-header .p-accordion-header-link) {
  padding: 0.5rem 0.65rem;
}

.form-overlays-block--dialog-compact :deep(.m-expansion .p-accordioncontent-content),
.form-overlays-block--dialog-compact :deep(.m-expansion .p-accordion-content) {
  padding: 0 0.5rem 0.5rem;
}

.form-overlays-block--dialog-sized {
  flex: 1 1 auto;
  min-height: min-content;
  display: flex;
  flex-direction: column;
}

.form-overlays-block--dialog-sized :deep(.form-top-actions),
.form-overlays-block--dialog-sized :deep(.form-actions-bottom) {
  flex: 0 0 auto;
}

.form-overlays-block--dialog-sized :deep(.form-section--header),
.form-overlays-block--dialog-sized :deep(.form-section--footer) {
  flex: 0 0 auto;
  min-height: auto;
  overflow: visible;
}

.form-overlays-block--dialog-sized :deep(.form-section--header .empty-tabs),
.form-overlays-block--dialog-sized :deep(.form-section--footer .empty-tabs) {
  flex: 0 0 auto;
  min-height: auto;
  overflow: visible;
}

.form-overlays-block--dialog-sized :deep(.form-main-body) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
}

/*
 * Sized dialog without leftover --fill (2673): Swing page JScrollPane — scroll
 * the dialog body, hug panel rows. Do not flex-shrink 4372 to feed 4400.
 */
.form-overlays-block--dialog-sized:not(:has(.modern-table--fill)):not(:has(.modern-tree-table--fill)) {
  flex: 0 0 auto;
  min-height: auto;
  overflow: visible;

  :deep(.form-main-body) {
    flex: 0 0 auto;
    min-height: auto;
    overflow: visible;
    display: block;
  }

  :deep(.tabs-container),
  :deep(.empty-tabs),
  :deep(.tab-content),
  :deep(.tab-panel) {
    flex: 0 0 auto;
    min-height: auto;
    height: auto;
    overflow: visible;
  }

  :deep(.panels-flex > .flex) {
    flex: 0 0 auto;
    min-height: auto;
  }
}

/*
 * WUI-526: leftover + inner table scroll only when a fill-table is in main
 * (1084). Do not clip sized dialogs without --fill (2577 TILES / 2673): they
 * scroll .m-dialog-body-wrap. Do NOT set height:0 on accordion.
 */
.form-overlays-block--dialog-sized :deep(.form-main-body:has(.modern-tree-table--fill)),
.form-overlays-block--dialog-sized :deep(.form-main-body:has(.modern-table--fill)),
.form-overlays-block--viewport :deep(.form-main-body:has(.modern-tree-table--fill)),
.form-overlays-block--viewport :deep(.form-main-body:has(.modern-table--fill)) {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  scrollbar-gutter: auto;

  > *,
  .tabs-container,
  .empty-tabs,
  .panels-flex {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /*
   * Page rows without a leftover table hug content (form 2673: 4383 / 4372).
   * Default flex-shrink:1 let 4400 eat 4372. Swing GridBag: weighty=0 keeps
   * preferred height; only the AUTO/wantGrow row (4400) gets leftover.
   */
  .panels-flex > .flex {
    flex: 0 0 auto;
    min-height: auto;
    overflow: visible;
  }

  .panels-flex > .flex:has(.modern-tree-table--fill),
  .panels-flex > .flex:has(.modern-table--fill) {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap !important;
    align-items: stretch !important;

    > .m-col {
      flex: 1 1 0% !important;
      width: 100% !important;
      max-width: 100% !important;
      min-height: 0 !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
    }

    .m-expansion,
    .p-accordion,
    .p-accordionpanel,
    .expansion-content,
    .panel-content-main,
    .panel-content {
      flex: 1 1 0%;
      min-height: 0;
      display: flex !important;
      flex-direction: column !important;
      overflow: hidden;
    }

    /*
     * PrimeVue AccordionContent uses v-show (display:none when collapsed).
     * Unconditional display:flex !important kept the table visible (2717 4436).
     */
    .p-accordionpanel-active,
    .p-accordioncontent[data-p-active='true'],
    .p-accordioncontent[data-p-active='true'] .p-accordioncontent-content {
      flex: 1 1 0%;
      min-height: 0;
      display: flex !important;
      flex-direction: column !important;
      overflow: hidden;
    }

    .elements-row:has(.modern-tree-table--fill),
    .elements-row:has(.modern-table--fill) {
      flex: 1 1 0%;
      min-height: 0;
      flex-wrap: nowrap !important;
      align-items: stretch !important;
      overflow: hidden;
    }

    .element-container.element-type-table,
    .element-container.element-type-treetable,
    .element-container.element-type-editable,
    .element-container.element-type-listbox {
      flex: 1 1 auto;
      min-height: 0 !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;

      .element-container-row {
        flex: 1 1 0%;
        min-height: 0;
        flex-wrap: nowrap !important;
        align-items: stretch !important;
        overflow: hidden;
      }

      .element-content-wrapper,
      .element-content {
        flex: 1 1 0%;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
    }

    .modern-table--fill,
    .modern-tree-table--fill {
      flex: 1 1 0%;
      min-height: calc(var(--platform-table-fill-min-body, 120px) + 2.75rem);
      overflow: hidden;
    }

    .tab-content {
      overflow: hidden;
      scrollbar-gutter: auto;
    }
  }

  .panels-flex > .flex:has(.modern-table--fill):has(.p-accordionpanel-active),
  .panels-flex > .flex:has(.modern-tree-table--fill):has(.p-accordionpanel-active),
  .panels-flex > .flex:has(.modern-table--fill):has(.m-expansion--no-header),
  .panels-flex > .flex:has(.modern-tree-table--fill):has(.m-expansion--no-header) {
    flex: 1 1 0%;
    min-height: calc(var(--platform-table-fill-min-body, 120px) + 5rem);
    overflow: hidden;
  }

  .panels-flex > .flex:has(.modern-table--fill):has(.p-accordionheader):not(:has(.p-accordionpanel-active)),
  .panels-flex > .flex:has(.modern-tree-table--fill):has(.p-accordionheader):not(:has(.p-accordionpanel-active)) {
    flex: 0 0 auto;
    min-height: auto;
    overflow: visible;
  }
}

.form-overlays-block--dialog-sized :deep(.form-main-body:has(.modern-table--fill)),
.form-overlays-block--dialog-sized :deep(.form-main-body:has(.modern-tree-table--fill)) {
  min-height: min-content;

  > *,
  .tabs-container,
  .empty-tabs,
  .panels-flex {
    min-height: min-content;
  }
}

/*
 * Document page (form.grabVertical=false) with a fill table and sibling panels
 * (2921 «Новый заказ»: sites table, then policy / RKN / consents). Leftover clip
 * made .empty-tabs overflow:hidden, so panels under the table were cut off and
 * neither the page nor the table showed a bar. Hug the rows; scroll main once.
 * A page style wrapper is display:contents, so .panels-flex is not a direct
 * child of .empty-tabs; hug that descendant too or the sheet clips again.
 * A page whose only row is the table keeps leftover. Do not nest :has() — the
 * browser drops the whole rule. A non-growing sibling row is .flex without
 * .panels-flex-row--grow (Block.vue marks grabVertical rows).
 * Do not rewrite flex on ordinary columns: flex-basis is their width.
 */
.form-overlays-block--viewport.form-overlays-block--scroll-page :deep(
  .form-main-body:has(.modern-table--fill, .modern-tree-table--fill)
  :is(.empty-tabs, .tab-content):has(.panels-flex > .flex:not(.panels-flex-row--grow))
) {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;

  > .panels-flex,
  .element-style-wrapper .panels-flex,
  .tab-panel .panels-flex {
    flex: 0 0 auto;
    height: auto;
    min-height: auto;
    max-height: none;
    overflow: visible;
  }

  /* Fill row is a column; flex here is height. Sibling rows keep their widths. */
  .panels-flex > .flex:is(:has(.modern-table--fill), :has(.modern-tree-table--fill)) {
    flex: 0 0 auto !important;
    height: auto !important;
    min-height: auto !important;
    max-height: none !important;

    > .m-col,
    .m-expansion,
    .p-accordion,
    .p-accordionpanel,
    .p-accordioncontent,
    .p-accordioncontent-content,
    .expansion-content,
    .panel-content,
    .panel-content-main,
    .elements-row:is(:has(.modern-table--fill), :has(.modern-tree-table--fill)),
    .modern-table--fill,
    .modern-tree-table--fill,
    .modern-table__body-wrap,
    .modern-tree-table__body-wrap
    {
      flex: 0 0 auto !important;
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
    }

    > .m-col,
    .modern-table--fill,
    .modern-tree-table--fill,
    .modern-table__body-wrap,
    .modern-tree-table__body-wrap {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
    }

    .element-container:is(.element-type-table, .element-type-treetable, .element-type-editable, .element-type-listbox),
    .element-container-row,
    .element-body,
    .element-content-wrapper,
    .element-content,
    .element-renderer {
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
    }

    .modern-table__body-wrap,
    .modern-tree-table__body-wrap {
      overflow-x: auto !important;
      overflow-y: hidden !important;
    }
  }
}
</style>
